import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LayoutCollapseContext,
  type LayoutCollapseContextType,
} from "./LayoutCollapse.context";

const LayoutCollapseProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [isSubheaderCollapsed, setIsSubheaderCollapsed] = useState(false);
  const [isLegendCollapsed, setIsLegendCollapsed] = useState(false);

  const toggleHeader = useCallback(() => {
    const newValue = !isHeaderCollapsed;
    setIsHeaderCollapsed(newValue);
    setIsSubheaderCollapsed(newValue);
    setIsLegendCollapsed(newValue);
  }, [isHeaderCollapsed]);

  const toggleSubheader = useCallback(() => {
    setIsSubheaderCollapsed((prev) => !prev);
  }, []);

  const toggleLegend = useCallback(() => {
    setIsLegendCollapsed((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isHeaderCollapsed) return () => {};

    const handleKeyDown = (e: KeyboardEvent) => {
      const keyIsEscape = e.key === "Escape";

      if (keyIsEscape && !document.querySelector(".clb-drawer-overlay")) {
        toggleHeader();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isHeaderCollapsed, toggleHeader]);

  const value = useMemo<LayoutCollapseContextType>(
    () => ({
      isHeaderCollapsed,
      isSubheaderCollapsed,
      isLegendCollapsed,
      toggleHeader,
      toggleSubheader,
      toggleLegend,
    }),
    [
      isHeaderCollapsed,
      isSubheaderCollapsed,
      isLegendCollapsed,
      toggleHeader,
      toggleSubheader,
      toggleLegend,
    ]
  );

  const cssVars = {
    "--current-header-height": isHeaderCollapsed ? "0px" : "48px",
    "--current-subheader-height": isSubheaderCollapsed ? "10px" : "56px",
    "--current-legend-height": isLegendCollapsed ? "8px" : "48px",
  } as React.CSSProperties;

  return (
    <LayoutCollapseContext.Provider value={value}>
      <div style={cssVars}>{children}</div>
    </LayoutCollapseContext.Provider>
  );
};

export default LayoutCollapseProvider;
